import { Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';

import { useOrder, useOtpTimer } from '@/logic/state';

import { FormattedMessage } from 'react-intl';
import axios from 'axios';

import shallow from 'zustand/shallow';
import { useDialog } from '@/logic/state/dialog';
import { useSendOtp } from '@/logic/services/otp';
import { useSnack } from '@/logic/state/snackbar';
import { useVerifyGuestOtp } from '@/logic/services';
import {
  VerificationCodeForm,
  VerificationCodeFormModel,
} from '@/components/shared/verification-code-form';
import ImageBoxFuture from '@/components/shared/image-box-future';
const emailIcon = '/icons/shared/emailIcon.svg';

export const GuestOtpForm: FC = () => {
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const setGuestBookingInfo = useOrder(s => s.setGuestBookingInfo, shallow);
  const [otpTimer, setOtpTimer] = useOtpTimer(s => [s.otpTimer, s.setOtpTimer], shallow);
  const isTimerRunning = otpTimer?.isRunning;

  const reSendOtp = useSendOtp();
  const verifyGuestOtp = useVerifyGuestOtp();

  const onCloseDialog = useCallback(() => {
    setDialog({});
    setOtpTimer({
      timerVal: 0,
      isRunning: true,
    });
  }, [setDialog, setOtpTimer]);

  const handleFormSubmit = useCallback(
    (data: VerificationCodeFormModel) => {
      setError(false);
      verifyGuestOtp.safeMutateAsync(
        {
          data: {
            ...data,
            phoneNumber: dialog?.phoneNumberFormPayload?.phoneNumber,
          },
        },
        {
          onSuccess: res => {
            onCloseDialog();
            setGuestBookingInfo(prevState => ({
              ...prevState,
              guestUserId: res?.guestUser?.id,
              isGuestVerified: res?.guestUser?.isVerified,
            }));
          },
          onError: error => {
            if (axios.isAxiosError(error)) {
              setSnack({
                isOpen: true,
                message: 'checkout.guest.otp.error',
                type: 'error',
              });
              setError(true);
            }
          },
        }
      );
    },
    [
      dialog?.phoneNumberFormPayload?.phoneNumber,
      onCloseDialog,
      setGuestBookingInfo,
      setSnack,
      verifyGuestOtp,
    ]
  );

  const handleCodeResend = useCallback(() => {
    setError(false);
    setOtpTimer(prevState => ({
      ...prevState,
      timerVal: 0,
    }));
    reSendOtp.safeMutateAsync(
      {
        data: {
          phoneNumber: dialog?.phoneNumberFormPayload?.phoneNumber,
        },
      },
      {
        onSuccess: res => {
          setOtpTimer({
            timerVal: res?.retryAfter,
            isRunning: true,
          });
          setSnack({
            isOpen: true,
            message: 'otp.resent.successfully',
            icon: (
              <ImageBoxFuture
                img={emailIcon}
                objectFit='contain'
                sx={{
                  width: '16px',
                  height: '16px',
                }}
              />
            ),
            type: 'info',
          });
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            setOtpTimer({
              timerVal: 0,
              isRunning: true,
            });
          }
        },
      }
    );
  }, [dialog?.phoneNumberFormPayload?.phoneNumber, reSendOtp, setOtpTimer, setSnack]);

  useEffect(() => {
    if (!isTimerRunning && !error) {
      setSnack({
        isOpen: true,
        message: 'checkout.guest.otp.warning',
        type: 'warning',
        colorOfText: 'common.white',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerRunning]);

  return (
    <VerificationCodeForm
      subtitle='verify.phone.number'
      description='global.description.code.phone_number'
      value={dialog?.phoneNumberFormPayload?.phoneNumber}
      isVerifyCodeLoading={verifyGuestOtp.isLoading}
      error={error}
      handleFormClose={onCloseDialog}
      handleFormSubmit={handleFormSubmit}
      handleCodeResend={handleCodeResend}
      isSendOtpLoading={reSendOtp?.isLoading}
      actionComponent={
        error ||
        (!isTimerRunning && (
          <Button variant='contained' fullWidth onClick={onCloseDialog}>
            <Typography component='span' sx={{ fontWeight: 'bold' }}>
              <FormattedMessage id={'checkout.guest.otp.continue_without_verify'} />
            </Typography>
          </Button>
        ))
      }
    />
  );
};
